import React, { useState } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { ICorporateEvent, ICorporateEventLocation } from "../../../../../interfaces/corporateEvent";
import { iAddress } from "../../../../../types/eventTypes";
import CarretDown from "../../../../../assets/svg-fc/CarretDown";
import {
	getFromCms,
	convertArrayToStringForCorporateEvent,
	groupLocationsByDayAndConvertToArray,
	openGoogleMap,
} from "../../../../../helpers/common";
import IsMobile from "../../../../../hooks/isMobile";
import CoreButtons from "../../../../core-components/button/CoreButtons";
import { BUTTON_VARIANT } from "../../../../../types/coreButtonTypes";
import { iDetailListItemProps } from "../../../../../types/contentType";
import { useAppSelector } from "../../../../../hooks/storeHooks";
import {
	DEFAULT_DATE_FORMAT_2,
	formatDateToTimeString,
	formatStringToTimeString,
	TIME_FORMAT_12H_CAPITAL,
	TIME_INPUT_FORMAT,
} from "../../../../../utils/formatTime";
import CorporateEventDetailListItem from "../../../../reusable-components/b2b/corporate-event-detail-list-item/corporateEventDetailListItem";
import "./ScheduleLocationsTabContent.scss";

interface ScheduleLocationsTabContentProps {
	corporateEventDetail: ICorporateEvent;
	OnClickTriggerLeftContainerScroll?: () => void;
}

const ScheduleLocationsTabContent: React.FC<ScheduleLocationsTabContentProps> = ({
	corporateEventDetail,
	OnClickTriggerLeftContainerScroll,
}) => {
	const isMobile = IsMobile();
	const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
	const [selectedDay, setSelectedDay] = useState<number>(1);
	const cms = useAppSelector((state) => state.events.cms);

	const handleAccordionToggle = (accordionId: string) => {
		OnClickTriggerLeftContainerScroll?.();
		setExpandedAccordion((prev) => (prev === accordionId ? null : accordionId));
	};

	// Group locations by day using utility function
	const locationsByDay = groupLocationsByDayAndConvertToArray(corporateEventDetail?.locations || []);

	if (Object.keys(locationsByDay).length === 0) {
		return (
			<Box className="scheduleLocationsTabContent">
				<Box className="noLocations">
					<Typography variant="body1">No locations have been added to this event yet.</Typography>
				</Box>
			</Box>
		);
	}

	const AddressComponent = ({ address }: { address: iAddress | string }) => {
		return (
			<Box className="itemAddressValue">
				<Typography className="value">
					{typeof address === "string" ? address : address?.addressLine2 || address?.place_name || "N/A"}
				</Typography>
				<CoreButtons
					variant={BUTTON_VARIANT.OUTLINED}
					className="openLocation"
					onclick={() => openGoogleMap(address)}
					buttonText="Open in google maps"
				/>
			</Box>
		);
	};

	const getLocationDetailList = (location: ICorporateEventLocation): iDetailListItemProps[] => {
		const address = location?.address || location?.mapAddress;
		const emirate = location?.emirate ? getFromCms(location?.emirate, cms) : "N/A";

		return [
			{
				title: "Address",
				renderValue: () => <AddressComponent address={address} />,
			},
			{
				title: "Emirates",
				value: emirate,
			},
			{
				title: "Number of guests",
				value: location.numberOfGuests,
			},
			{
				title: "Type of provider",
				value: location.providerType?.value ? getFromCms(location.providerType?.value, cms) : "",
			},
			{
				title: "Start time",
				value: formatStringToTimeString(location.startTime, TIME_INPUT_FORMAT, TIME_FORMAT_12H_CAPITAL),
			},
			{
				title: "End time",
				value: formatStringToTimeString(location.endTime, TIME_INPUT_FORMAT, TIME_FORMAT_12H_CAPITAL),
			},
			{
				title: "Cuisines",
				value:
					location?.cuisines?.length > 0
						? convertArrayToStringForCorporateEvent(location?.cuisines, cms)
						: "",
			},
			{
				title: "Service style",
				value:
					location?.providerOptions?.length > 0
						? convertArrayToStringForCorporateEvent(location.providerOptions, cms)
						: "",
			},
			{
				title: "Courses",
				value:
					location?.courses?.length > 0 ? convertArrayToStringForCorporateEvent(location.courses, cms) : "",
			},
		];
	};

	const getTimeOfDay = (location: ICorporateEventLocation) => {
		return location?.timeOfDayMultiple?.length > 0
			? convertArrayToStringForCorporateEvent(location.timeOfDayMultiple, cms)
			: location?.timeOfDay
				? getFromCms(location.timeOfDay, cms)
				: "";
	};

	const renderLocations = (dayLocations: ICorporateEventLocation[]) => {
		return dayLocations.map((location, locationIndex) => {
			const address = location?.address || location?.mapAddress;
			return (
				<Box key={locationIndex} className="dayContent">
					<Box className="locationSection">
						<Typography className="locationTitle">
							{`- Location & Time ${locationIndex + 1} - ${typeof address === "string" ? address : address?.addressLine2 || address?.place_name || "N/A"}`}
						</Typography>

						<Box className="accordianDivider" />

						{isMobile && (
							<CorporateEventDetailListItem
								detailListItemClass="locationAddress"
								title={getLocationDetailList(location)[0]?.title}
								renderValue={() => <AddressComponent address={address} />}
							/>
						)}

						<Box className="locationDetailsGrid">
							{(isMobile ? getLocationDetailList(location).slice(1) : getLocationDetailList(location)).map(
								(item, index) => (
									<CorporateEventDetailListItem
										key={index}
										title={item.title}
										value={item.value}
										renderValue={item.renderValue}
									/>
								),
							)}
						</Box>

						<CorporateEventDetailListItem
							detailListItemClass="singleLineItem"
							title="Time of day"
							value={getTimeOfDay(location)}
						/>

						<CorporateEventDetailListItem
							detailListItemClass="singleLineItem"
							title="Additional Information"
							value={location.additionalInfo}
						/>
					</Box>
				</Box>
			);
		});
	};

	return (
		<Box className="scheduleLocationsTabContent">
			{isMobile ? (
				<>
					<Box className="addCorporateEventFormDays">
						{Array.from({ length: corporateEventDetail?.numberOfDays || 1 }).map((_, index) => {
							const dayNumber = index + 1;
							return (
								<Box
									key={index}
									className={`addCorporateEventFormDay ${dayNumber === selectedDay ? "active" : ""}`}
									onClick={() => setSelectedDay(dayNumber)}
								>
									<Typography
										className={`addCorporateEventFormDayText ${dayNumber === selectedDay ? "active" : ""}`}
									>
										Day {dayNumber}
									</Typography>
								</Box>
							);
						})}
					</Box>
					{renderLocations(locationsByDay[selectedDay])}
				</>
			) : (
				<Box className="daysContainer">
					{Object.entries(locationsByDay).map(([dayNumber, dayLocations]) => {
						const accordionId = `day-${dayNumber}`;
						const isExpanded = expandedAccordion === accordionId;

						// Calculate totals for the day
						const firstLocation = dayLocations[0];

						return (
							<Accordion
								key={accordionId}
								expanded={isExpanded}
								onChange={() => handleAccordionToggle(accordionId)}
								className="dayAccordion"
								disableGutters
							>
								<AccordionSummary
									expandIcon={
										<Box className="expandIcon">
											<CarretDown />
										</Box>
									}
									className="accordionSummary"
									classes={{
										content: "accordionContent",
										expandIconWrapper: "expandIconWrapper",
									}}
								>
									<Box className="dayHeader">
										<Typography className="dayTitle">
											Day {dayNumber} -{" "}
											{formatDateToTimeString(firstLocation.eventDate, DEFAULT_DATE_FORMAT_2)}
										</Typography>
										<Typography className="daySummary">
											{dayLocations.length} location{dayLocations.length > 1 ? "s" : ""}
										</Typography>
									</Box>
								</AccordionSummary>

								<AccordionDetails>{renderLocations(dayLocations)}</AccordionDetails>
							</Accordion>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export default ScheduleLocationsTabContent;
